import { useEffect, useMemo, useState } from "react";
import useSWRImmutable from "swr/immutable";
import {
    blockUsers,
    PasswordRenewalInterval,
    SesstionDuration as SessionDuration,
    setMFA,
    setPasswordRenewalInterval,
    setSessionDuration,
} from "../api/local-admin";
import AppButton from "../components/AppButton";
import BottomNavigationButtons from "../components/BottomNavigationButtons";
import { twMerge } from "tailwind-merge";
import useConfirm from "../hooks/useConfirm";
import Select from "../components/forms/Select";
import useFeatureFlags from "../hooks/useFeatureFlags";

interface TypedUserAttribute {
    Name:
        | "email"
        | "email_verified"
        | "phone_number"
        | "phone_number_verified"
        | "custom:force_password_reset"
        | "custom:signer_name"
        | "custom:signature_id"
        | "custom:session_duration"
        | "custom:renewal_interval"
        | "custom:avatar"
        | "custom:password_last_set"
        | "custom:role"
        | "sub"
        | "mfa_enabled"
        | "is_blocked";
    Value: string;
}

type TypedUserData = {
    [username: string]: TypedUserAttribute[];
};

type FormattedUserData = {
    username: string;
    email: string;
    phone_number: string;
    email_verified: boolean;
    phone_number_verified: boolean;
    custom: {
        force_password_reset: boolean;
        signer_name: string;
        signature_id: string;
        session_duration: SessionDuration | null;
        renewal_interval: PasswordRenewalInterval | null;
        avatar: string;
        password_last_set: string | null;
        role: string;
    };
    sub: string;
    mfa_enabled: boolean;
    is_blocked: boolean;
};
function LocalAdminDashboard() {
    const [selectedUsername, setSelectedUsername] = useState<string | null>(
        null
    );
    const [mfaEnabled, setMfaEnabled] = useState(true);
    const [sessionDuration, setSessionDurationValue] =
        useState<null | SessionDuration>(null);
    const [passwordInterval, setPasswordInterval] =
        useState<null | PasswordRenewalInterval>(null);
    const [error, setError] = useState<string | null>(null);
    const { show } = useConfirm();

    const { data: usersByAttributes, mutate: mutateUsers } =
        useSWRImmutable<TypedUserData[]>("/admin/users");
    const { featureFlags } = useFeatureFlags();

    const users: Array<FormattedUserData> = useMemo(
        () =>
            usersByAttributes
                ? usersByAttributes.map((userByAttributes) => {
                      const [username, attributes] =
                          Object.entries(userByAttributes)[0];

                      const userData: FormattedUserData = {
                          username,
                          email: "",
                          phone_number: "",
                          email_verified: false,
                          phone_number_verified: false,
                          custom: {
                              force_password_reset: false,
                              signer_name: "",
                              signature_id: "",
                              session_duration: null,
                              renewal_interval: null,
                              avatar: "",
                              password_last_set: null,
                              role: "",
                          },
                          sub: "",
                          mfa_enabled: false,
                          is_blocked: false,
                      };
                      attributes.forEach((attr) => {
                          if (attr.Name === "email") {
                              userData.email = attr.Value;
                          } else if (attr.Name === "phone_number") {
                              userData.phone_number = attr.Value;
                          } else if (attr.Name === "email_verified") {
                              userData.email_verified = attr.Value === "true";
                          } else if (attr.Name === "phone_number_verified") {
                              userData.phone_number_verified =
                                  attr.Value === "true";
                          } else if (
                              attr.Name === "custom:force_password_reset"
                          ) {
                              userData.custom.force_password_reset =
                                  attr.Value === "true";
                          } else if (attr.Name === "custom:signer_name") {
                              userData.custom.signer_name = attr.Value;
                          } else if (attr.Name === "custom:signature_id") {
                              userData.custom.signature_id = attr.Value;
                          } else if (attr.Name === "custom:session_duration") {
                              userData.custom.session_duration =
                                  +attr.Value as SessionDuration;
                          } else if (attr.Name === "custom:renewal_interval") {
                              userData.custom.renewal_interval =
                                  +attr.Value as PasswordRenewalInterval;
                          } else if (attr.Name === "custom:avatar") {
                              userData.custom.avatar = attr.Value;
                          } else if (attr.Name === "custom:password_last_set") {
                              userData.custom.password_last_set = attr.Value;
                          } else if (attr.Name === "custom:role") {
                              userData.custom.role = attr.Value;
                          } else if (attr.Name === "sub") {
                              userData.sub = attr.Value;
                          } else if (attr.Name === "mfa_enabled") {
                              userData.mfa_enabled = Boolean(attr.Value);
                          } else if (attr.Name === "is_blocked") {
                              userData.is_blocked = Boolean(attr.Value);
                          }
                      });
                      return userData;
                  })
                : [],
        [usersByAttributes]
    );

    const selectedUser = useMemo(
        () => users.find((user) => user.username === selectedUsername),
        [selectedUsername, users]
    );

    const sessionDurationOptions: Array<{
        id: string;
        value: SessionDuration;
        label: string;
    }> = [
        { id: "3", value: 3, label: "3 minutes" },
        { id: "10", value: 10, label: "10 minutes" },
        { id: "30", value: 30, label: "30 minutes" },
        { id: "60", value: 60, label: "1 hour" },
        { id: "120", value: 120, label: "2 hours" },
        { id: "180", value: 180, label: "3 hours" },
    ];

    const passwordIntervalOptions: Array<{
        id: string;
        value: PasswordRenewalInterval;
        label: string;
    }> = [
        { id: "7", value: 7, label: "7 days" },
        { id: "30", value: 30, label: "30 days" },
        { id: "90", value: 90, label: "90 days" },
        { id: "180", value: 180, label: "180 days" },
    ];

    useEffect(() => {
        if (selectedUser) {
            setMfaEnabled(selectedUser.mfa_enabled);
            setSessionDurationValue(
                selectedUser.custom.session_duration || null
            );
            setPasswordInterval(selectedUser.custom.renewal_interval || null);
        } else {
            setMfaEnabled(true);
            setSessionDurationValue(null);
            setPasswordInterval(null);
        }
    }, [selectedUser]);

    const toggleBlockAccount = async () => {
        if (!selectedUsername) return;

        show({
            title: `Are you sure you want to ${
                selectedUser!.is_blocked ? "unblock" : "block"
            } this user?`,
            confirmText: selectedUser!.is_blocked ? "Unblock" : "Block",
            onConfirm: async () => {
                try {
                    await blockUsers(
                        selectedUsername,
                        !selectedUser!.is_blocked
                    );
                    // Refresh users list
                    await mutateUsers();
                } catch (err) {
                    setError("Failed to block account");
                }
            },
        });
    };

    // MARK: onConfirm
    const onConfirm = async () => {
        if (!selectedUser) return;
        const promises: Promise<any>[] = [];
        if (mfaEnabled !== selectedUser.mfa_enabled) {
            promises.push(setMFA(selectedUser.username, mfaEnabled));
        }
        if (
            sessionDuration &&
            sessionDuration !== selectedUser.custom.session_duration
        ) {
            promises.push(
                setSessionDuration(selectedUser.username, sessionDuration)
            );
        }
        if (
            passwordInterval &&
            passwordInterval !== selectedUser.custom.renewal_interval
        ) {
            promises.push(
                setPasswordRenewalInterval(
                    selectedUser.username,
                    passwordInterval
                )
            );
        }

        show({
            onConfirm: async () => {
                try {
                    await Promise.all(promises);
                    // Refresh users list
                    await mutateUsers();
                } catch (err) {
                    setError("Failed to apply changes");
                }
            },
        });
    };

    const fieldsAreDisabled = !selectedUser || selectedUser.is_blocked;

    const disable2FAUpdate =
        fieldsAreDisabled || !featureFlags?.allow_disabling_2fa;

    // MARK: Render
    return (
        <div className="isWhitePage p-6">
            <form className="max-w-md ms-40">
                <h1 className="text-2xl font-bold mb-6">Administration Page</h1>

                {error && (
                    <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                        {error}
                        <button
                            onClick={() => setError(null)}
                            className="ml-2 text-red-800 hover:text-red-900"
                        >
                            ×
                        </button>
                    </div>
                )}
                <div>
                    {/* User Selection */}
                    <div className="mb-16">
                        <Select
                            value={selectedUsername || ""}
                            onChange={(value) =>
                                setSelectedUsername(value as string)
                            }
                            options={[
                                ...(users?.map((user) => ({
                                    id: user.username,
                                    value: user.username,
                                    label: user.username || user.email,
                                })) || []),
                            ]}
                            disabled={!users}
                            placeholder="Select a user"
                            buttonClassName="h-8 xl:h-10"
                            optionsClassName="w-[450px] ml-0"
                        />
                    </div>

                    {/* Action Buttons */}
                    <div className="space-y-8 mb-16">
                        <AppButton
                            onClick={toggleBlockAccount}
                            type="button"
                            disabled={!selectedUser}
                        >
                            {selectedUser?.is_blocked ? "Unblock" : "Block"}{" "}
                            Account
                        </AppButton>
                    </div>

                    {/* MFA Setting */}
                    <div
                        className={twMerge(
                            "mb-6 flex items-center",
                            disable2FAUpdate && "opacity-50"
                        )}
                    >
                        <label className="flex items-center space-x-3">
                            <input
                                type="checkbox"
                                checked={!mfaEnabled}
                                onChange={(e) => {
                                    setMfaEnabled(!e.target.checked);
                                }}
                                disabled={disable2FAUpdate}
                                className="form-checkbox h-5 w-5 text-blue-600"
                            />
                            <span className="">
                                Disabling two-factor authentication
                            </span>
                        </label>
                    </div>

                    {/* Session Duration */}
                    <div
                        className={twMerge(
                            "mb-6 flex items-baseline gap-2",
                            fieldsAreDisabled && "opacity-50"
                        )}
                    >
                        <label className="block whitespace-nowrap mb-2">
                            Set login session expiration
                        </label>
                        <Select
                            value={sessionDuration || ""}
                            onChange={(value) =>
                                setSessionDurationValue(
                                    +value as SessionDuration
                                )
                            }
                            disabled={fieldsAreDisabled}
                            options={sessionDurationOptions}
                            placeholder="Select duration"
                        />
                    </div>

                    {/* Password Renewal Interval */}
                    <div
                        className={twMerge(
                            "mb-8 flex items-baseline gap-2",
                            fieldsAreDisabled && "opacity-50"
                        )}
                    >
                        <label className="block whitespace-nowrap mb-2">
                            Force password renewal interval
                        </label>
                        <Select
                            value={passwordInterval || ""}
                            onChange={(value) =>
                                setPasswordInterval(
                                    +value as PasswordRenewalInterval
                                )
                            }
                            disabled={fieldsAreDisabled}
                            options={passwordIntervalOptions}
                            placeholder="Select interval"
                        />
                    </div>
                </div>
                {/* Confirm and Cancel Buttons */}
                <BottomNavigationButtons
                    onCancelClick={() => {
                        setSelectedUsername(null);
                    }}
                    isFixed
                    isCancelDisabled={!selectedUser}
                    isConfirmDisabled={!selectedUser}
                    onConfirmClick={onConfirm}
                    confirmText={"Confirm"}
                />
            </form>
        </div>
    );
}

export default LocalAdminDashboard;
